import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import SectionCard, { SectionCardList, SectionCardEmpty } from '../../../components/ui/SectionCard';

/**
 * "Active sessions" dashboard section — in-progress test sessions the
 * technician can resume.
 *
 * Presentational: the caller owns fetching and formatting, so this stays
 * trivially testable and does not depend on the API shape. Hides the row
 * layout, the live-session dot, and the empty state.
 *
 * Props:
 *   sessions  — Array<{ id, siteName, meta, canResume?, ownerLabel?, resumeLabel? }>; `meta`
 *               is display-ready text such as 'Electrical · Started 20 Apr 2026'
 *   onResume  — (session) => void
 *   onDelete  — (session) => void; admin-only cleanup of any user's session
 *   canDeleteSessions — when true, renders an admin-only Delete action per row
 *   number    — eyebrow passed through to SectionCard
 *   isLoading — shows a loading line instead of the list
 */
export default function ActiveSessionsCard({
  sessions = [],
  onResume,
  onDelete,
  canDeleteSessions = false,
  number,
  isLoading = false,
}) {
  return (
    <SectionCard number={number} title="Active Sessions" aria-label="Active sessions">
      {isLoading ? (
        <SectionCardEmpty>Loading sessions…</SectionCardEmpty>
      ) : sessions.length === 0 ? (
        <SectionCardEmpty>No active sessions.</SectionCardEmpty>
      ) : (
        <SectionCardList>
          {sessions.map((session) => (
            <li key={session.id} className="section-card-row">
              <span className="section-card-row-dot" aria-hidden="true" />
              <div className="section-card-row-main">
                <span className="section-card-row-name">{session.siteName}</span>
                <span className="section-card-row-meta">{session.meta}</span>
              </div>
              <div className="section-card-row-actions">
                {session.canResume === false ? (
                  <Badge variant="muted">{session.ownerLabel}</Badge>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="section-card-row-action"
                    onClick={() => onResume?.(session)}
                    aria-label={session.resumeLabel ?? `Resume testing at ${session.siteName}`}
                  >
                    Resume
                  </Button>
                )}
                {canDeleteSessions && (
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    className="section-card-row-action"
                    onClick={() => onDelete?.(session)}
                    aria-label={`Delete session at ${session.siteName}`}
                  >
                    Delete
                  </Button>
                )}
              </div>
            </li>
          ))}
        </SectionCardList>
      )}
    </SectionCard>
  );
}
